import type { FilterDef, FilterOption, ActiveFilter } from "./types";
import i18n from "@/i18n";
import {
  FILTER_OPERATORS,
  FILTER_ARRAY_VALUES_SEPARATOR,
  FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR
} from "@/data/table";

/** Date operators whose value may be rendered as a relative-time phrase. */
export const DATE_RELATIVE_OPERATORS = ["after", "before", "ar", "br"];

/**
 * UI operators that don't take a user-entered value — the op label alone
 * describes the filter. Pills skip the value segment for these.
 */
export const VALUELESS_UI_OPERATORS = [
  "is_true",
  "is_false",
  "is_set",
  "is_not_set"
];

/**
 * UI-level operator → API operator + value transform.
 *
 * Some user-facing operators (Contains, Is true, On or after date, …) share
 * an underlying API operator but differ by value shape or input UI. We store
 * the UI operator on the ActiveFilter so edit flows can reconstruct the
 * original UI, then translate to the API op at serialization time.
 *
 * Entries listed here override the default op-passthrough behaviour in
 * `defaultToParam` and are recognised by `getOperatorLabel` for pill labels.
 */
export const UI_OPERATOR_MAP: Record<
  string,
  { apiOp: string; transform: (value: any) => string; labelKey: string }
> = {
  contains: {
    apiOp: "like",
    transform: v => `%${String(v ?? "")}%`,
    labelKey: "_.contains"
  },
  starts_with: {
    apiOp: "like",
    transform: v => `${String(v ?? "")}%`,
    labelKey: "_.starts_with"
  },
  ends_with: {
    apiOp: "like",
    transform: v => `%${String(v ?? "")}`,
    labelKey: "_.ends_with"
  },
  is_true: { apiOp: "eq", transform: () => "1", labelKey: "_.is_true" },
  is_false: { apiOp: "eq", transform: () => "0", labelKey: "_.is_false" },
  is_set: { apiOp: "neq", transform: () => "null", labelKey: "_.is_set" },
  is_not_set: { apiOp: "eq", transform: () => "null", labelKey: "_.is_not_set" }
};

/** Unit key → i18n plural translation key. Shared by FilterModal and formatRelativeFilter. */
export const UNIT_PLURAL_KEYS: Record<string, string> = {
  minutes: "_plurals.unit_minute",
  hours: "_plurals.unit_hour",
  days: "_plurals.unit_day",
  weeks: "_plurals.unit_week",
  months: "_plurals.unit_month",
  years: "_plurals.unit_year"
};

/** Maps UI concept → API operator + value sign. Shared by FilterModal and formatRelativeFilter. */
export const CONCEPT_MAP: Record<string, { op: string; sign: string }> = {
  in_the_last: { op: "after", sign: "-" },
  older_than: { op: "before", sign: "-" },
  in_the_next: { op: "before", sign: "+" },
  more_than: { op: "after", sign: "+" }
};

/** API operator → i18n key fallback, used when `FILTER_OPERATORS()` has no match. */
const OP_LABEL_FALLBACK_KEYS: Record<string, string> = {
  eq: "_.is",
  neq: "_.is_not",
  like: "_.contains",
  gt: "_.is_greater_than",
  gte: "_.is_greater_or_equal",
  lt: "_.is_less_than",
  lte: "_.is_less_than_or_equal_to",
  after: "_.after",
  before: "_.before",
  any: "_.matches_any_of",
  all: "_.matches_all_of",
  none: "_.matches_none_of"
};

/** Map composable operator strings → ApiOperators values for label lookup */
const OP_API_MAP: Record<string, string> = {
  eq: "", // ApiOperators.DEFAULT → EQUAL label
  neq: "neq",
  like: "like",
  gt: "gt",
  gte: "gte",
  lt: "lt",
  lte: "lte",
  after: "after",
  before: "before",
  any: "any",
  all: "all",
  none: "none"
};

/**
 * Resolves a human readable label for an API operator.
 * Attempts to use the localized i18n FILTER_OPERATORS list before falling back safely.
 *
 * Memoised per-locale — called per filter pill per render, and the underlying
 * `FILTER_OPERATORS()` rebuilds a 12-entry i18n-translated array on every call.
 */
const operatorLabelCache = new Map<string, Map<string, string>>();
export function getOperatorLabel(apiOp: string): string {
  const locale = i18n.locale;
  let localeCache = operatorLabelCache.get(locale);
  if (!localeCache) {
    localeCache = new Map();
    operatorLabelCache.set(locale, localeCache);
  }
  const cached = localeCache.get(apiOp);
  if (cached !== undefined) return cached;

  // UI-level operators (contains, is_true, on_or_after, …) resolve via a
  // direct i18n key rather than the FILTER_OPERATORS list.
  const uiEntry = UI_OPERATOR_MAP[apiOp];
  if (uiEntry) {
    const label = String(i18n.t(uiEntry.labelKey));
    localeCache.set(apiOp, label);
    return label;
  }

  const ops = FILTER_OPERATORS();
  // Try key-based match first (for tag operators: any, all, none)
  const keyMatch = ops.find(o => o.key === apiOp);
  if (keyMatch) {
    const label = String(keyMatch.label);
    localeCache.set(apiOp, label);
    return label;
  }

  const mapped = OP_API_MAP[apiOp] ?? apiOp;
  const match = ops.find(o => o.apiOperator === mapped);
  if (match) {
    const label = String(match.label);
    localeCache.set(apiOp, label);
    return label;
  }

  const fallbackKey =
    OP_LABEL_FALLBACK_KEYS[apiOp] ?? OP_LABEL_FALLBACK_KEYS.eq;
  const label = String(i18n.t(fallbackKey));
  localeCache.set(apiOp, label);
  return label;
}

/**
 * Generates a human readable string for the chosen values of a filter.
 * Pass resolved options to display labels instead of raw IDs.
 */
export function formatFilterValue(
  value: any,
  options?: FilterOption[]
): string {
  if (value === undefined || value === null || value === "") return "";

  // Walks options + their nested `children` (tree-select inputs).
  const findOption = (
    opts: FilterOption[],
    predicate: (o: FilterOption) => boolean
  ): FilterOption | undefined => {
    for (const o of opts) {
      if (predicate(o)) return o;
      if (o.children?.length) {
        const hit = findOption(o.children, predicate);
        if (hit) return hit;
      }
    }
    return undefined;
  };

  if (Array.isArray(value) && options?.length) {
    const labels: string[] = [];
    const consumed = new Set<string>();

    for (const v of value) {
      if (consumed.has(String(v))) continue;

      const opt = findOption(options, (o: FilterOption) => {
        if (String(o.value) === String(v)) return true;
        if (
          typeof o.value === "string" &&
          o.value.includes(FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR)
        ) {
          return o.value
            .split(FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR)
            .includes(String(v));
        }
        return false;
      });

      if (opt && !consumed.has(String(opt.value))) {
        labels.push(String(opt.label));
        if (
          typeof opt.value === "string" &&
          opt.value.includes(FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR)
        ) {
          opt.value
            .split(FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR)
            .forEach((s: string) => consumed.add(s));
        } else {
          consumed.add(String(opt.value));
        }
      } else if (!opt) {
        labels.push(String(v));
      }
    }
    return labels.join(", ");
  }

  if (Array.isArray(value)) return value.join(", ");

  if (options?.length) {
    const opt = findOption(
      options,
      (o: FilterOption) => String(o.value) === String(value)
    );
    if (opt) return String(opt.label);
  }

  return String(value);
}

const ARRAY_SEP_RE = new RegExp(FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR, "g");

/**
 * Standard generator for API `filter[field|operator] = value` parameter objects.
 */
export function defaultToParam(
  field: string,
  defaultOp: string
): NonNullable<FilterDef["toParam"]> {
  return (value: any, operator?: string) => {
    const op = operator || defaultOp;

    // UI operators (e.g. `contains`, `is_true`, `on_or_after`) carry a
    // different API op + value transform; resolve those first so the API
    // receives the backend-native form.
    const uiEntry = UI_OPERATOR_MAP[op];
    if (uiEntry) {
      const apiOp = uiEntry.apiOp;
      const suffix = apiOp && apiOp !== "eq" ? `|${apiOp}` : "";
      return { [`filter[${field}${suffix}]`]: uiEntry.transform(value) };
    }

    // Map abstract operators to actual API operators
    // "any" means equality across array values (e.g. IN), "none" means NOT IN
    const apiOp = op === "any" ? "eq" : op === "none" ? "neq" : op;
    const suffix = apiOp && apiOp !== "eq" ? `|${apiOp}` : "";

    const formatted = Array.isArray(value)
      ? value
          .map(v =>
            String(v).replace(ARRAY_SEP_RE, FILTER_ARRAY_VALUES_SEPARATOR)
          )
          .join(FILTER_ARRAY_VALUES_SEPARATOR)
      : String(value);

    return { [`filter[${field}${suffix}]`]: formatted };
  };
}

/**
 * Build an API filter map from active filters and their definitions. Output
 * keys use the wrapped `filter[field|op]` format expected by the backend list
 * endpoints — same shape `useListing.buildApiParams` produces internally.
 */
export function buildApiFilterParams(
  active: ActiveFilter[],
  defs: FilterDef[]
): Record<string, any> {
  const params: Record<string, any> = {};
  for (const af of active) {
    const def =
      defs.find(d => d.id === af.defId) || defs.find(d => d.field === af.field);
    if (def?.toParam) {
      Object.assign(params, def.toParam(af.value, af.operator));
    } else {
      Object.assign(
        params,
        defaultToParam(af.field, af.operator)(af.value, af.operator)
      );
    }
    if (def?.requires) Object.assign(params, def.requires);
  }
  return params;
}

/**
 * Build a bare-key filter map (no `filter[...]` wrapper) for segment storage.
 * Segments persist their filter clause as `{ "field|op": value }` and the
 * backend wraps it when running the segment.
 */
export function buildSegmentFilterParams(
  active: ActiveFilter[],
  defs: FilterDef[]
): Record<string, any> {
  const wrapped = buildApiFilterParams(active, defs);
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(wrapped)) {
    const m = key.match(/^filter\[(.+)\]$/);
    out[m ? m[1] : key] = value;
  }
  return out;
}

/**
 * Formats a relative date string into a human readable phrase using the exact same translation
 * map keys as FilterModal (e.g., 'Within the last week', 'More than 2 weeks ago').
 */
export function formatRelativeFilter(operator: string, value: string): string {
  if (!value || typeof value !== "string") return value;

  const sign = value.substring(0, 1);
  const idx = value.indexOf("_");
  if (idx === -1) return value;

  const n = Number(value.substring(1, idx)) || 1;
  const unitRaw = value.substring(idx + 1);
  const pluralKey = UNIT_PLURAL_KEYS[unitRaw];
  const unitT = pluralKey ? i18n.tc(pluralKey, n) : unitRaw;

  const op =
    operator === "ar" ? "after" : operator === "br" ? "before" : operator;

  const phraseKeys: Record<string, string> = {
    "after,-": "_datetime.relative.within_past",
    "before,-": "_datetime.relative.more_past",
    "before,+": "_datetime.relative.within_future",
    "after,+": "_datetime.relative.more_future"
  };
  const phraseKey = phraseKeys[`${op},${sign}`];
  if (phraseKey) return i18n.tc(phraseKey, n, { n, unit: unitT });

  return value;
}
